import {useEffect, useState} from 'react';
import {WeddingOfficiantDropdown} from '../components/WeddingOfficiantDropdown';
import {DisplayMessage, WeddingOfficiantDB} from '../lib/interfaces';
import {t} from 'i18next';
import AbortBooking from '../components/AbortBooking';
import {useUserClaims} from '../hooks/useUserClaims';
import {useBookingState} from '../store';
import {BreakpointMinWidth, DisplayMessageTypes, RoutePath} from '../lib/enums';
import LoginButton from '../components/LoginButton';
import {useWindowSize} from '@uidotdev/usehooks';
import {ADDRESS_FIELD_MAX_LENGTH, CITY_FIELD_MAX_LENGTH} from '../utils/constants';
import {UaDateInput, UaDisplayMessage, UaInput, UaLink} from 'uppsala.ui.react';

export const CustomOccasionPage = () => {
  const size = useWindowSize();

  const [selectedOfficiant, setSelectedOfficiant] = useState<WeddingOfficiantDB>();
  const [occasionDate, setOccasionDate] = useState<string>();
  const [occasionTime, setOccasionTime] = useState<string>();
  const [occasionAddress, setOccasionAddress] = useState<string>();
  const [occasionCity, setOccasionCity] = useState<string>();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [errorDisplayMessage, setErrorDisplayMessage] = useState({visible: false} as DisplayMessage);
  const [loginButtonDisabled, setLoginButtonDisabled] = useState(true);

  const {setCustomOccasion, clearState} = useBookingState();
  const {userClaims} = useUserClaims();

  useEffect(() => {
    clearState();
    const fakeLoggedIn = sessionStorage.getItem('fakeLoggedIn');
    setIsLoggedIn(userClaims !== null || fakeLoggedIn !== null);
  }, [userClaims]);

  useEffect(() => {
    const invalid = !occasionDate || !occasionTime || !occasionAddress || !occasionCity || !selectedOfficiant;
    if (!invalid) {
      const dateAndTime = new Date(`${occasionDate}T${occasionTime}Z`).toISOString().replace('.000Z', '');
      setCustomOccasion(occasionAddress, occasionCity, selectedOfficiant, dateAndTime);
    }
    setLoginButtonDisabled(invalid);
  }, [occasionDate, occasionTime, occasionAddress, occasionCity, selectedOfficiant]);

  const handleOfficiantSelect = (officiant?: WeddingOfficiantDB) => {
    setSelectedOfficiant(officiant);
  };

  if (errorDisplayMessage.visible) {
    return (
      <>
        {errorDisplayMessage.visible && (
          <UaDisplayMessage
            style={{margin: '25px 0'}}
            type={errorDisplayMessage.type as DisplayMessageTypes}
            message={errorDisplayMessage.message}
          />
        )}
      </>
    );
  }

  return (
    <>
      <h1>{t('booking.header.createCustomOccasion')}</h1>
      <p>{t('booking.customOccasionInfo')}</p>

      <div style={{margin: '30px 0'}}>
        <UaLink
          text={t('Gå till vigselförrättarna')}
          link={'https://www.uppsala.se/stod-och-omsorg/vigsel-och-begravning/borgerlig-vigsel/vigselforrattare/'}
        />
      </div>

      <WeddingOfficiantDropdown
        label={t('booking.agreedUponOfficiant') ?? 'Vilken vigselförrättare har du kommit överens med?'}
        onSelect={handleOfficiantSelect}
        setDisplayMessage={(displayMessage) => {
          setErrorDisplayMessage(displayMessage);
        }}
        valid={selectedOfficiant ? true : undefined}
      />
      <div style={{marginTop: '40px'}}>
        <p>{t('booking.agreedUponDateTime')}</p>
        <div
          className="flex-row"
          style={{
            gap: size.width && size.width < BreakpointMinWidth.xs ? '0' : '10px',
            marginTop: '5px',
            display: 'flex',
            flexWrap: 'wrap',
          }}>
          <UaDateInput
            id={'custom-occasion-date'}
            label={'Välj datum'}
            value={occasionDate}
            onChange={(e) => setOccasionDate(e.target.value)}
            valid={occasionDate ? true : undefined}
            style={{width: '250px'}}
          />
          <UaInput
            id={'custom-time'}
            type={'time'}
            label={'Välj tid'}
            onChange={(e) => setOccasionTime(e.target.value)}
            value={occasionTime}
            valid={occasionTime ? true : undefined}
            style={{width: '250px'}}
          />
        </div>
      </div>

      <div style={{marginTop: '15px'}}>
        <p>{t('booking.agreedUponLocation')}</p>
        <div
          className="flex-row"
          style={{
            flexWrap: 'wrap',
            gap: size.width && size.width < BreakpointMinWidth.xs ? '0' : '10px',
            marginTop: '5px',
            display: 'flex',
          }}>
          <UaInput
            style={{width: '250px'}}
            id={'custom-address'}
            label={'Adress'}
            onChange={(e) => setOccasionAddress(e.target.value)}
            value={occasionAddress}
            valid={occasionAddress ? occasionAddress?.length <= ADDRESS_FIELD_MAX_LENGTH : undefined}
            invalidMessage={t('validation.fieldExceededMaxLength', {parameter: ADDRESS_FIELD_MAX_LENGTH}) ?? ''}
          />
          <UaInput
            style={{width: '250px'}}
            id={'custom-city'}
            label={'Ort'}
            onChange={(e) => setOccasionCity(e.target.value)}
            value={occasionCity}
            valid={occasionCity ? occasionCity?.length <= CITY_FIELD_MAX_LENGTH : undefined}
            invalidMessage={t('validation.fieldExceededMaxLength', {parameter: CITY_FIELD_MAX_LENGTH}) ?? ''}
          />
        </div>
      </div>

      <div style={{marginTop: '25px'}}>
        {!isLoggedIn && <label htmlFor="continueCustomBookTimeButton">{t('booking.loginIsRequired')}</label>}
        <div
          className="flex-row"
          style={{
            marginTop: '15px',
            gap: '15px',
            flexWrap: size.width && size.width < BreakpointMinWidth.xs ? 'wrap-reverse' : 'nowrap',
          }}>
          <AbortBooking />
          <LoginButton disabled={loginButtonDisabled} title={t('button.book')} returnUrl={RoutePath.ContactInfoPage} />
        </div>
      </div>
    </>
  );
};
